import {DsAvatar, PhoneHQTooltip} from "ch3-ui-lib";
import {Clock} from "lucide-react";
import {hexToEmoji} from "../../components/Emoji/emoji";
import {clsxMerge} from "../../utilis/classNameUtils.ts";
import {fullName, getTeamGroup, TeamGroup, TeamMember} from "./teamFilters";
import {formatUntil} from "./teamFormat";

const PRESENCE_LABEL: Record<TeamGroup, string> = {
    AVAILABLE: 'Available',
    IN_MEETING: 'In a meeting',
    IN_CALL: 'In a call',
    DND: 'Do not disturb',
    OFFLINE: 'Offline',
};

/**
 * Design System presence: told apart by shape, not colour alone — filled dot
 * (available, in a call), ring (in a meeting), dot with a minus (do not
 * disturb) and a hollow ring (offline).
 */
export const StatusDot = ({group, className, size = 10, labelled = false}: {
    group: TeamGroup;
    className?: string;
    /** 10px in chips and on 32px avatars, 14px on larger avatars. */
    size?: 10 | 14;
    /** Give the dot its own accessible name (on avatars); chips already say it in words. */
    labelled?: boolean;
}) => {
    const shape: Record<TeamGroup, string> = {
        AVAILABLE: 'bg-success',
        IN_MEETING: 'bg-surface shadow-[inset_0_0_0_2px_var(--status-busy)]',
        IN_CALL: 'bg-warning',
        DND: 'bg-status-dnd',
        OFFLINE: 'bg-surface shadow-[inset_0_0_0_2px_var(--status-offline)]',
    };
    return (
        <span
            role={labelled ? 'img' : undefined}
            aria-label={labelled ? PRESENCE_LABEL[group] : undefined}
            aria-hidden={labelled ? undefined : true}
            data-presence={group}
            className={clsxMerge('inline-flex shrink-0 items-center justify-center rounded-full', shape[group], className)}
            style={{width: size, height: size}}
        >
            {group === 'DND' && <span className="h-[2px] w-[6px] rounded-[1px] bg-surface"/>}
        </span>
    );
};

export const MemberAvatar = ({member, size = 'md'}: {member: TeamMember; size?: 'sm' | 'md'}) => (
    <DsAvatar
        size={size === 'sm' ? 28 : 32}
        name={fullName(member)}
        src={member.avatarUrl || undefined}
        badge={<StatusDot group={getTeamGroup(member)} size={14} labelled className="border-2 border-solid border-surface"/>}
    />
);

export const CustomStatus = ({member}: {member: TeamMember}) => {
    const status = member.customUserStatus;
    if (!status?.icon) return null;
    const until = formatUntil(status.dueDate);
    return (
        <div className="flex min-w-0 items-center text-sm">
            <PhoneHQTooltip content={status.name} align="start"><div className="flex min-w-0 items-center">
                    {hexToEmoji(status.icon, '!w-4 !h-4 mr-2')}
                    <span className="truncate">{status.name}</span>
                </div></PhoneHQTooltip>
            {until && <span className="ml-1 shrink-0 text-ink-muted">until {until}</span>}
        </div>
    );
};

const timeZoneAbbreviation = (timeZone: string): string => {
    try {
        return new Intl.DateTimeFormat('en-US', {timeZone, timeZoneName: 'short'})
            .formatToParts(new Date())
            .find(part => part.type === 'timeZoneName')?.value ?? '';
    } catch {
        return '';
    }
};

const localTime = (timeZone: string): string => {
    try {
        return new Date().toLocaleString('en-US', {timeZone, hour: '2-digit', minute: '2-digit', hour12: true});
    } catch {
        return '';
    }
};

/** "03:30 PM · CET"; `fallback` (or nothing) when the colleague has no time zone. */
export const LocalTimeWithZone = ({timeZone, className, fallback}: {timeZone: string; className?: string; fallback?: string}) => {
    const time = timeZone ? localTime(timeZone) : '';
    if (!time) {
        if (fallback === undefined) return null;
        return <div className={clsxMerge('flex items-center text-footnote text-ink-muted', className)}>{fallback}</div>;
    }
    const zone = timeZoneAbbreviation(timeZone);
    return (
        <div className={clsxMerge('flex items-center gap-1.5 text-footnote text-ink-muted', className)}>
            <Clock size={14} className="!p-0 shrink-0 !text-current"/>
            <span className="truncate">{zone ? `${time} · ${zone}` : time}</span>
        </div>
    );
};
